import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Pagination,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Collapse,
  Tooltip,
  Stack,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import HistoryIcon from '@mui/icons-material/History';
import api from '../../services/api';
import { formatDate } from '../../utils/formatDate';

const GRID_COLUMNS = {
  xs: '1fr',
  sm: '1fr 0.8fr 0.8fr',
  md: '30px 1.2fr 0.8fr 0.8fr 1fr 1.5fr 0.8fr',
};

// Color mapping for action types
const getActionColor = (action) => {
  const upper = (action || '').toUpperCase();
  if (upper.includes('CREATE')) return { bg: '#e8f5e9', color: '#2e7d32', border: '#4caf50' };
  if (upper.includes('UPDATE')) return { bg: '#e3f2fd', color: '#1565c0', border: '#42a5f5' };
  if (upper.includes('DELETE')) return { bg: '#ffebee', color: '#c62828', border: '#f44336' };
  return { bg: '#f5f5f5', color: '#666', border: '#bdbdbd' };
};

const Logs = () => {
  // State
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
  });

  // Filters
  const [filterAction, setFilterAction] = useState('');
  const [filterEntityType, setFilterEntityType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Filter options (loaded from API)
  const [actionOptions, setActionOptions] = useState([]);
  const [entityTypeOptions, setEntityTypeOptions] = useState([]);

  // Expanded row
  const [expandedRow, setExpandedRow] = useState(null);

  const limit = 20;

  // Fetch filter options
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const [actionsRes, entityTypesRes] = await Promise.all([
          api.get('/admin/logs/actions'),
          api.get('/admin/logs/entity-types'),
        ]);
        setActionOptions(actionsRes.data.data || actionsRes.data || []);
        setEntityTypeOptions(entityTypesRes.data.data || entityTypesRes.data || []);
      } catch (err) {
        console.error('Error fetching filter options:', err);
      }
    };

    fetchFilterOptions();
  }, []);

  // Fetch logs
  const fetchLogs = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const params = { page, limit };

      if (filterAction) params.action = filterAction;
      if (filterEntityType) params.entity_type = filterEntityType;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const response = await api.get('/admin/logs', { params });
      const { data, pagination: paginationData } = response.data;

      setLogs(data || []);
      setPagination({
        currentPage: paginationData?.currentPage || 1,
        totalPages: paginationData?.totalPages || 1,
        totalItems: paginationData?.totalItems || 0,
      });
    } catch (err) {
      console.error('Error fetching logs:', err);
      setError('Error al cargar los logs. Inténtalo de nuevo.');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [filterAction, filterEntityType, dateFrom, dateTo]);

  // Fetch on mount and filter changes
  useEffect(() => {
    fetchLogs(1);
  }, [fetchLogs]);

  // Page change
  const handlePageChange = (_event, newPage) => {
    fetchLogs(newPage);
  };

  // Row expand toggle
  const handleRowToggle = (logId) => {
    setExpandedRow((prev) => (prev === logId ? null : logId));
  };

  // Format JSON details for preview
  const formatDetailsPreview = (details) => {
    if (!details) return '-';
    try {
      const str = typeof details === 'string' ? details : JSON.stringify(details);
      return str.length > 100 ? str.substring(0, 100) + '...' : str;
    } catch {
      return '-';
    }
  };

  // Format JSON details for expanded view
  const formatDetailsExpanded = (details) => {
    if (!details) return 'Sin detalles';
    try {
      const obj = typeof details === 'string' ? JSON.parse(details) : details;
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(details);
    }
  };

  // Truncate UUID
  const truncateId = (id) => {
    if (!id) return '-';
    return id.length > 8 ? id.substring(0, 8) + '...' : id;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '100vw',
        px: { xs: 0, md: 2.5 },
        py: { xs: 0, md: 4 },
        pb: 0,
        mr: { xs: -6 },
        pr: { xs: 0 },
      }}
    >
      {/* Fixed Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          padding: { xs: '12px 16px', sm: '8px 16px', md: '12px 16px' },
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          backgroundColor: 'white',
          zIndex: 1000,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              ml: { xs: '50px', sm: '50px', md: 35.5 },
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontSize: { xs: '16px', sm: '18px', md: '20px', lg: '22px' },
                fontWeight: 'medium',
                color: '#000',
              }}
            >
              Logs de Actividad
              {!loading && (
                <Typography
                  component="span"
                  sx={{
                    fontSize: { xs: '14px', sm: '15px', md: '16px' },
                    fontWeight: 'normal',
                    color: '#666',
                    ml: 1,
                  }}
                >
                  ({pagination.totalItems})
                </Typography>
              )}
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                fontSize: { xs: '12px', sm: '15px', md: '14px' },
                color: '#666',
                mt: { xs: 0, md: 0.3 },
                display: { xs: 'none', sm: 'none', md: 'none', lg: 'block' },
              }}
            >
              Registro de acciones realizadas en la plataforma
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Spacer for fixed header */}
      <Box
        sx={{
          height: { xs: '65px', sm: '70px', md: '100px', lg: '90px' },
          '@media (max-width: 600px) and (min-resolution: 1.25dppx)': { height: '65px' },
          '@media (max-width: 600px) and (min-resolution: 1.5dppx)': { height: '90px' },
          '@media (min-width: 600px) and (min-resolution: 1.25dppx)': { height: '75px' },
          '@media (min-width: 900px) and (min-resolution: 1.25dppx)': { height: '95px' },
        }}
      />

      {/* Main content */}
      <Box sx={{ ml: { xs: -6, md: 0 } }}>
        <Box sx={{ paddingLeft: { xs: 6, md: 2 }, paddingRight: { xs: 0, md: 4 } }}>
          {/* Error state */}
          {error && (
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ color: '#c62828', fontSize: '14px' }}>
                {error}
              </Typography>
            </Box>
          )}

          {/* Loading state */}
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '400px',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <CircularProgress sx={{ color: '#0F52BD' }} />
              <Typography variant="body2" sx={{ color: '#666' }}>
                Cargando logs...
              </Typography>
            </Box>
          ) : logs.length === 0 ? (
            /* Empty state */
            <Paper
              elevation={0}
              sx={{
                p: 6,
                textAlign: 'center',
                boxShadow: '0px 0px 10px 2px rgba(98, 113, 137, 0.46)',
                borderRadius: 2.5,
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: '#000', fontWeight: 'medium' }}
              >
                No se encontraron logs
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: '#666' }}
              >
                {filterAction || filterEntityType || dateFrom || dateTo
                  ? 'No hay logs que coincidan con los filtros aplicados.'
                  : 'Aún no hay registros de actividad en la plataforma.'}
              </Typography>
            </Paper>
          ) : (
            /* Data Card */
            <Paper
              elevation={0}
              sx={{
                pt: 4,
                pl: 4,
                pr: 3,
                pb: 3,
                position: 'relative',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                boxShadow: '0px 0px 10px 2px rgba(98, 113, 137, 0.46)',
                borderRadius: 2.5,
                overflow: 'hidden',
              }}
            >
              {/* Card header: Icon + Title */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <HistoryIcon sx={{ fontSize: 28, mr: 1, color: '#000' }} />
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ fontSize: '18px', color: '#000' }}
                  >
                    Logs
                  </Typography>
                </Box>
              </Box>

              {/* Filters row */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  mb: 3,
                  flexWrap: 'wrap',
                  alignItems: 'center',
                }}
              >
                <FormControl size="small" sx={{ minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
                  <InputLabel>Acción</InputLabel>
                  <Select
                    value={filterAction}
                    onChange={(e) => setFilterAction(e.target.value)}
                    label="Acción"
                  >
                    <MenuItem value="">Todas</MenuItem>
                    {actionOptions.map((action) => (
                      <MenuItem key={action} value={action}>
                        {action}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
                  <InputLabel>Tipo de entidad</InputLabel>
                  <Select
                    value={filterEntityType}
                    onChange={(e) => setFilterEntityType(e.target.value)}
                    label="Tipo de entidad"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {entityTypeOptions.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Desde"
                  type="date"
                  size="small"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 160, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                />

                <TextField
                  label="Hasta"
                  type="date"
                  size="small"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 160, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                />
              </Box>

              {/* Grid data list */}
              <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
                {/* Grid Headers - desktop only */}
                <Box
                  sx={{
                    display: { xs: 'none', md: 'grid' },
                    gridTemplateColumns: GRID_COLUMNS.md,
                    gap: 2,
                    py: 1,
                    borderBottom: '2px solid #E0E0E0',
                    mb: 1,
                    alignItems: 'center',
                  }}
                >
                  {/* Empty header for expand icon */}
                  <Box />
                  {['Fecha', 'Acción', 'Tipo', 'ID Entidad', 'Detalles', 'Admin'].map((label) => (
                    <Typography
                      key={label}
                      variant="h6"
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '14px',
                        color: '#000',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {label}
                    </Typography>
                  ))}
                </Box>

                {/* Grid Rows */}
                <Stack spacing={0}>
                  {logs.map((log) => {
                    const logId = log.log_id || log.id;
                    const isExpanded = expandedRow === logId;
                    const actionColors = getActionColor(log.action);

                    return (
                      <React.Fragment key={logId}>
                        <Box
                          onClick={() => handleRowToggle(logId)}
                          sx={{
                            display: { xs: 'block', sm: 'grid' },
                            gridTemplateColumns: { sm: GRID_COLUMNS.sm, md: GRID_COLUMNS.md },
                            gap: 2,
                            py: 2,
                            alignItems: 'center',
                            borderBottom: '1px solid #E0E0E0',
                            cursor: 'pointer',
                            transition: 'background-color 0.15s ease',
                            '&:hover': { backgroundColor: '#f5f8ff' },
                            ...(isExpanded && { backgroundColor: '#f5f8ff' }),
                          }}
                        >
                          {/* Mobile view - stacked card */}
                          <Box
                            sx={{
                              display: { xs: 'block', sm: 'none' },
                              p: 2,
                              border: '1px solid #F0F0F0',
                              borderRadius: '8px',
                              backgroundColor: isExpanded ? '#f5f8ff' : '#FAFAFA',
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography sx={{ fontSize: '13px', color: '#666' }}>
                                {formatDate(log.created_at, { includeTime: true })}
                              </Typography>
                              {isExpanded
                                ? <KeyboardArrowUpIcon sx={{ color: '#666', fontSize: 20 }} />
                                : <KeyboardArrowDownIcon sx={{ color: '#666', fontSize: 20 }} />
                              }
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
                              <Chip
                                label={log.action || '-'}
                                size="small"
                                sx={{
                                  backgroundColor: actionColors.bg,
                                  color: actionColors.color,
                                  border: `1px solid ${actionColors.border}`,
                                  fontWeight: 'medium',
                                  fontSize: '12px',
                                }}
                              />
                              <Typography sx={{ fontSize: '14px', color: '#4F4F4F' }}>
                                {log.entity_type || '-'}
                              </Typography>
                            </Box>
                            <Typography sx={{ fontSize: '13px', color: '#666', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {formatDetailsPreview(log.details)}
                            </Typography>
                          </Box>

                          {/* Tablet view - 3 columns */}
                          <Box sx={{ display: { xs: 'none', sm: 'block', md: 'none' } }}>
                            <Typography sx={{ fontSize: '14px', color: '#4F4F4F' }}>
                              {formatDate(log.created_at, { includeTime: true })}
                            </Typography>
                          </Box>
                          <Box sx={{ display: { xs: 'none', sm: 'flex', md: 'none' } }}>
                            <Chip
                              label={log.action || '-'}
                              size="small"
                              sx={{
                                backgroundColor: actionColors.bg,
                                color: actionColors.color,
                                border: `1px solid ${actionColors.border}`,
                                fontWeight: 'medium',
                                fontSize: '12px',
                              }}
                            />
                          </Box>
                          <Box sx={{ display: { xs: 'none', sm: 'block', md: 'none' } }}>
                            <Typography sx={{ fontSize: '14px', color: '#4F4F4F' }}>
                              {log.entity_type || '-'}
                            </Typography>
                          </Box>

                          {/* Desktop view - 7 columns */}
                          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', justifyContent: 'center' }}>
                            {isExpanded
                              ? <KeyboardArrowUpIcon sx={{ color: '#666', fontSize: 20 }} />
                              : <KeyboardArrowDownIcon sx={{ color: '#666', fontSize: 20 }} />
                            }
                          </Box>
                          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                            <Typography sx={{ fontSize: '14px', color: '#4F4F4F', whiteSpace: 'nowrap' }}>
                              {formatDate(log.created_at, { includeTime: true })}
                            </Typography>
                          </Box>
                          <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                            <Chip
                              label={log.action || '-'}
                              size="small"
                              sx={{
                                backgroundColor: actionColors.bg,
                                color: actionColors.color,
                                border: `1px solid ${actionColors.border}`,
                                fontWeight: 'medium',
                                fontSize: '12px',
                              }}
                            />
                          </Box>
                          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                            <Typography sx={{ fontSize: '14px', color: '#4F4F4F' }}>
                              {log.entity_type || '-'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                            <Tooltip title={log.entity_id || ''} arrow placement="top">
                              <Typography sx={{ fontSize: '13px', color: '#4F4F4F', fontFamily: 'monospace' }}>
                                {truncateId(log.entity_id)}
                              </Typography>
                            </Tooltip>
                          </Box>
                          <Box sx={{ display: { xs: 'none', md: 'block' }, overflow: 'hidden' }}>
                            <Typography sx={{ fontSize: '13px', color: '#666', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {formatDetailsPreview(log.details)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                            <Tooltip title={log.admin_user_id || ''} arrow placement="top">
                              <Typography sx={{ fontSize: '13px', color: '#4F4F4F', fontFamily: 'monospace' }}>
                                {truncateId(log.admin_user_id)}
                              </Typography>
                            </Tooltip>
                          </Box>
                        </Box>

                        {/* Expanded details */}
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 2, borderBottom: '1px solid #E0E0E0' }}>
                            <Typography
                              sx={{
                                fontSize: '13px',
                                fontWeight: 'bold',
                                color: '#000',
                                mb: 1,
                              }}
                            >
                              Detalles completos:
                            </Typography>
                            <Paper
                              elevation={0}
                              sx={{
                                p: 2,
                                backgroundColor: '#fafafa',
                                borderRadius: 1.5,
                                border: '1px solid #E0E0E0',
                                maxHeight: 300,
                                overflow: 'auto',
                              }}
                            >
                              <pre
                                style={{
                                  margin: 0,
                                  fontFamily: 'monospace',
                                  fontSize: '13px',
                                  color: '#4F4F4F',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                }}
                              >
                                {formatDetailsExpanded(log.details)}
                              </pre>
                            </Paper>
                            {log.admin_user_id && (
                              <Typography
                                sx={{ fontSize: '12px', color: '#666', mt: 1 }}
                              >
                                Admin ID completo: {log.admin_user_id}
                              </Typography>
                            )}
                            {log.entity_id && (
                              <Typography
                                sx={{ fontSize: '12px', color: '#666', mt: 0.5 }}
                              >
                                Entity ID completo: {log.entity_id}
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </React.Fragment>
                    );
                  })}
                </Stack>
              </Box>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pt: 2,
                    mt: 2,
                    borderTop: '1px solid #E0E0E0',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 2, sm: 0 },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#666',
                      fontSize: '14px',
                      textAlign: { xs: 'center', sm: 'left' },
                    }}
                  >
                    {`Mostrando ${(pagination.currentPage - 1) * limit + 1} - ${Math.min(
                      pagination.currentPage * limit,
                      pagination.totalItems
                    )} de ${pagination.totalItems} logs`}
                  </Typography>
                  <Pagination
                    count={pagination.totalPages}
                    page={pagination.currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    shape="rounded"
                    size="small"
                    sx={{
                      '& .MuiPaginationItem-root': {
                        fontSize: '14px',
                        minWidth: '32px',
                        height: '32px',
                      },
                      '& .Mui-selected': {
                        backgroundColor: '#0F52BD !important',
                        color: 'white',
                      },
                      '& .MuiPaginationItem-root:hover': {
                        backgroundColor: '#E3F2FD',
                      },
                      '& .MuiPaginationItem-previousNext': {
                        border: '1px solid #E0E0E0',
                      },
                    }}
                  />
                </Box>
              )}
            </Paper>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Logs;
